const nlBlogCondens = {
  meta: {
    title: "Condens op Ramen Oplossen | Tips & Oorzaken",
    description:
      "Condens op ramen binnenkant, buitenkant of tussen dubbel glas ? Ontdek de oorzaken en effectieve oplossingen tegen condensvorming en vocht.",
  },
  hero: {
    h1: "Condens op ramen : oorzaken en oplossingen",
    subtitle:
      "Condens op ramen is een veelvoorkomend probleem. Ontdek wat de oorzaken zijn, wanneer het schadelijk is en hoe u het effectief aanpakt met ventilatie, isolatie of glasvervanging.",
    image:
      "/img/04-home-interior-visible-condensation.jpg",
    updated: "Laatst bijgewerkt mei 2026",
    readTime: "6 min leestijd",
  },
  toc: {
    title: "Inhoudsopgave",
    items: [
      "Wat veroorzaakt condens op ramen ?",
      "Condens op de binnenkant van ramen",
      "Condens op de buitenkant van ramen",
      "Condens tussen dubbel glas (lekke ruit)",
      "Wanneer professioneel ingrijpen ?",
      "Veelgestelde vragen",
    ],
  },
  sections: {
    oorzaken: {
      h2: "Wat veroorzaakt condens op ramen ?",
      p1: "Condensvorming op ramen ontstaat wanneer vochtige, warme lucht in contact komt met een koud oppervlak. Het raam koelt de lucht af tot het dauwpunt, waardoor waterdamp condenseert tot vloeibaar water. Dit is een puur natuurkundig proces, maar kan wel serieuze gevolgen hebben voor uw woning.",
      p2: "De belangrijkste oorzaken van condens op ramen zijn: te hoge luchtvochtigheid binnenshuis, grote temperatuurverschillen tussen binnen en buiten, slechte ventilatie die vochtige lucht niet afvoert, en koudebruggen rondom raamkozijnen. Ook de kwaliteit van het glas speelt een doorslaggevende rol.",
      p3: "In oude woningen met enkel glas of verouderd dubbel glas is condens vrijwel onvermijdelbaar in het stookseizoen. In moderne woningen met HR++ of triple glas treedt condens veel minder op, omdat het oppervlak van het glas warmer blijft en dus minder snel onder het dauwpunt komt.",
      causes: [
        { title: "Te veel vocht in huis", text: "Koken, douchen, kleding drogen en zelfs ademen produceren vocht. Zonder afdoende ventilatie stapelt dit vocht op." },
        { title: "Slechte ventilatie", text: "Moderne woningen zijn luchtdicht gebouwd. Zonder mechanische ventilatie of regelmatig luchten blijft vocht hangen." },
        { title: "Grote temperatuurverschil", text: "Bij strenge vorst kan het temperatuurverschil tussen binnen en buiten oplopen tot 25 graden. Het glas koelt dan sterk af." },
        { title: "Oud of beschadigd glas", text: "Enkel glas of beschadigd isolatieglas leidt tot koude oppervlakken waar condens onmiddellijk ontstaat." },
      ],
    },
    binnenkant: {
      h2: "Condens op de binnenkant van ramen",
      p1: "Condens aan de binnenkant van het raam is het meest voorkomende type. Het duidt op een relatief hoge luchtvochtigheid in de ruimte, gecombineerd met een koud glasoppervlak. Dit is met name zichtbaar in slaapkamers, badkamers en keukens.",
      p2: "Hoewel het op zich niet direct schadelijk is, kan aanhoudende condens leiden tot schimmelvorming op vensterbanken en kozijnen. Houten kozijnen kunnen hierdoor rotten en de luchtkwaliteit in huis verslechtert. Kinderkamers zijn extra gevoelig voor dit probleem.",
      oplossingen: [
        { title: "Ventileren", text: "Zet een raam op een kiertje, ook in de winter. Een ventilatierooster of mechanische ventilatie is nog effectiever en comfortabeler." },
        { title: "Ontvochtigen", text: "Een ontvochtiger in vochtige ruimtes (badkamer, kelder) verlaagt de relatieve luchtvochtigheid en voorkomt condens." },
        { title: "HR++ glas plaatsen", text: "HR++ glas heeft een betere isolatiewaarde, waardoor de binnenkant van het raam warmer blijft en condens minder snel ontstaat." },
        { title: "Ventilatieroosters", text: "Ventilatieroosters in het raam of kozijn zorgen voor een constante luchtstroom zonder warmteverlies." },
      ],
    },
    buitenkant: {
      h2: "Condens op de buitenkant van ramen",
      p1: "Condens aan de buitenkant van uw ramen kan verwarrend lijken, maar is eigenlijk een positief teken. Het duidt erop dat uw glas zeer goed isoleert. De binnenkant van het glas blijft warm, waardoor de buitenkant extra sterk afkoelt en condens ontstaat op de buitenzijde.",
      p2: "Dit fenomeen treedt vooral op bij triple glas en hoogwaardig HR++ glas op heldere, koude ochtenden. Er is hierbij geen sprake van een defect of lek. De condens verdampt vanzelf wanneer de zon op het raam schijnt of de temperatuur stijgt.",
      conclusion: "Kortom : condens aan de buitenkant is een teken van uitstekende isolatie. Er is geen actie nodig en het vormt geen risico voor uw woning of gezondheid.",
    },
    tussenGlas: {
      h2: "Condens tussen dubbel glas (lekke ruit)",
      p1: "Condens tussen de twee glasbladen van dubbel glas is een serieus probleem. Het duidt op een lek in het glaspakket, waardij de isolerende luchtvulling of het gas is ontsnapt. Het glas verliest hierdoor zijn isolerende eigenschappen en vocht dringt naar binnen.",
      p2: "U herkent een lekke ruit aan een wazig, melkachtig of beslagen uiterlijk tussen de glasplaten. Dit verdwijnt niet door ventileren of ontvochtigen, omdat het vocht zich binnen het glaspakket bevindt. In sommige gevallen ontstaat er zelfs schimmel tussen het glas.",
      p3: "De enige duurzame oplossing voor een lekke ruit is het vervangen van het glas. Onze specialisten meten het glas op locatie, bestellen het juiste type isolatieglas en plaatsen het zorgvuldig in het bestaande kozijn.",
      cta: {
        text: "Glas vervangen bij een lekke ruit",
        href: "/glas-vervangen/",
      },
    },
    professioneel: {
      h2: "Wanneer professioneel ingrijpen ?",
      p1: "In de meeste gevallen kunt u condens zelf aanpakken door te ventileren en de luchtvochtigheid te beheersen. Er zijn echter situaties waarbij professionele hulp wenselijk of noodzakelijk is.",
      situations: [
        { title: "Lekke ruit", text: "Condens tussen dubbel glas kan alleen opgelost worden door het glas te vervangen. Dit is vakwerk dat wij voor u uitvoeren." },
        { title: "Schimmel op kozijnen", text: "Aanhoudende vochtplekken en schimmel op houten kozijnen duiden op structurele problemen. Kozijnreparatie of vervanging kan nodig zijn." },
        { title: "Houtrot door vocht", text: "Langdurige vochtexpositie kan houtrot veroorzaken in kozijnen en vensterbanken. Vroegtijdig herstel voorkomt grotere schade." },
        { title: "Enkel glas vervangen", text: "Enkel glas is de belangrijkste oorzaak van condensproblemen. Vervanging door HR++ glas lost het probleem structureel op." },
      ],
      cta: {
        text: "Kozijnreparatie of glas vervangen",
        href: "/kozijn-reparatie/",
      },
    },
  },
  faq: {
    h2: "Veelgestelde vragen over condens op ramen",
    items: [
      {
        q: "Is condens tussen dubbel glas te repareren ?",
        a: "Nee, condens tussen dubbel glas is niet te repareren. De ruit is lek en de isolerende lucht of gas is ontsnapt. Het glas moet vervangen worden door een nieuw isolatieglas.",
      },
      {
        q: "Hoe voorkom ik condens op mijn ramen ?",
        a: "Ventileer regelmatig, gebruik een ontvochtiger in vochtige ruimtes, zet de verwarming op een constante temperatuur en overweeg HR++ glas voor een warmer glasoppervlak.",
      },
      {
        q: "Is condens op de buitenkant van ramen slecht ?",
        a: "Nee, condens aan de buitenkant is juist een teken van goed isolerend glas. Het glas houdt de warmte binnen, waardoor de buitenkant sterker afkoelt. Dit is niet schadelijk.",
      },
      {
        q: "Kan condens houtrot veroorzaken ?",
        a: "Ja, aanhoudende condens kan houtrot veroorzaken in houten kozijnen en vensterbanken. Ook schimmel kan ontstaan. Bij structurele schade is reparatie of vervanging van het kozijn nodig.",
      },
    ],
  },
  cta: {
    text: "Lekke ruit of condens tussen glas ? Vraag advies aan",
    phone: "040 798 7470",
  },
  schema: {
    headline: "Condens op ramen : oorzaken en oplossingen",
    description:
      "Condens op ramen binnenkant, buitenkant of tussen dubbel glas ? Ontdek de oorzaken en effectieve oplossingen tegen condensvorming en vocht.",
    author: "Ramen Repair Eindhoven",
    datePublished: "2026-05-28",
    dateModified: "2026-05-28",
  },
};

export default nlBlogCondens;